import { Fragment, type PropsWithChildren, type ReactElement, type ReactNode } from "react";
import { usePage } from "@inertiajs/react";
import { Eyebrow, H1, H2 } from "@/Components/DataDisplay";
import { PartnerBrand } from "@/Components/Partner";
import { LanguageSelector } from "@/Components/System";
import { useTranslations } from "@/hooks/use-translations";
import { type PageProps } from "@/types";

interface PartnerAuthLayoutProps extends PropsWithChildren {
    /** The page title on the form card — "Welcome back." — the one serif heading of the screen. */
    title: ReactNode;
    subtitle?: string;
}

/**
 * The split sign-in chrome of the partner portal (spec/partner-portal-plan.md
 * §2.2, §17.1, §18): a brand panel on the `--brand-*` tokens — tenant logo,
 * eyebrow, display headline whose later lines take the brand accent, value
 * prop, legal footer — beside the form card on `--brand-form-surface`. The
 * form card carries the page title (`H1`, the only serif heading); the
 * brand headline is a subheading, so it is sans like every other one.
 *
 * The copy comes from the `partnerSignIn` shared prop (the tenant's
 * partner-store settings per locale, its invoicing legal name and
 * registration number), so nothing here names a product: the tenant is the
 * brand (D7), and "Novazelle" appears only in the powered-by line.
 */
export default function PartnerAuthLayout({ title, subtitle, children }: PartnerAuthLayoutProps): ReactElement {
    const { t } = useTranslations();
    const { partnerSignIn, tenant } = usePage<PageProps>().props;

    const name = partnerSignIn?.tenant_name ?? tenant?.name ?? "";
    const logoUrl = partnerSignIn?.logo_url ?? tenant?.logo_url ?? null;
    const headlineLines = (partnerSignIn?.headline ?? "").split("\n").filter((line) => line.trim() !== "");
    const legalName = partnerSignIn?.legal_name ?? name;
    const registrationNumber = partnerSignIn?.registration_number ?? null;

    return (
        <div className="grid min-h-screen lg:grid-cols-[1fr_1.2fr]">
            <aside className="relative flex flex-col justify-between gap-10 overflow-hidden border-b border-border bg-brand-surface p-8 lg:border-b-0 lg:border-r lg:p-14">
                {/* Hairline diagonal texture, as in the prototype's brand panel. */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(135deg,transparent_0_60px,hsl(var(--brand-accent)/0.06)_60px_61px)]"
                />
                <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-brand-accent via-brand-accent-hover to-transparent" />

                <div className="relative z-10">
                    <PartnerBrand name={name} logoUrl={logoUrl} tone="panel" />
                </div>

                <div className="relative z-10 max-w-[560px]">
                    {partnerSignIn?.subline !== undefined && partnerSignIn.subline !== "" && (
                        <Eyebrow className="mb-4 text-brand-muted">{partnerSignIn.subline}</Eyebrow>
                    )}
                    <H2 size="lg" className="text-4xl leading-[1.02] tracking-[-0.025em] text-white lg:text-[clamp(40px,4.6vw,64px)]">
                        {headlineLines.map((line, index) =>
                            index === 0 ? (
                                <Fragment key={line}>{line}</Fragment>
                            ) : (
                                <Fragment key={line}>
                                    <br />
                                    <em className="italic text-brand-accent">{line}</em>
                                </Fragment>
                            ),
                        )}
                    </H2>
                    {partnerSignIn?.value_prop !== undefined && partnerSignIn.value_prop !== "" && (
                        <p className="mt-6 max-w-[48ch] text-base leading-relaxed text-brand-muted">{partnerSignIn.value_prop}</p>
                    )}
                </div>

                <div className="relative z-10 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-xs text-brand-muted-dim">
                    <span>
                        {t("partner.auth.legal", { year: String(new Date().getFullYear()), name: legalName })}
                        {registrationNumber !== null && (
                            <>
                                {" · "}
                                <span className="font-mono">{t("partner.auth.registration_number", { number: registrationNumber })}</span>
                            </>
                        )}
                    </span>
                    <span className="text-brand-muted-dimmer">{t("partner.auth.powered_by")}</span>
                </div>
            </aside>

            <main className="relative flex items-center justify-center bg-brand-form-surface px-6 py-12 lg:p-14">
                <div className="absolute right-4 top-4">
                    <LanguageSelector />
                </div>

                <div className="w-full max-w-[380px]">
                    <H1 size="lg">{title}</H1>
                    {subtitle !== undefined && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
                    <div className="mt-8">{children}</div>
                </div>
            </main>
        </div>
    );
}
